/* eslint-disable */
import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  Chip,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  useTheme,
  styled,
  Container,
  IconButton,
  Tooltip,
  Collapse,
} from "@mui/material";
import {
  IconKey,
  IconCode,
  IconBook,
  IconApi,
  IconShieldLock,
  IconRocket,
  IconBrandGraphql,
  IconCopy,
  IconCheck,
  IconChevronDown,
  IconChevronRight,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import Breadcrumb from "../shared/Breadcrumb";

// ─── Breadcrumb ───────────────────────────────────────────────────────────────
const BCrumb = [
  { to: "/", title: "Home" },
  { title: "API Documentation" },
];

// ─── Styled components ────────────────────────────────────────────────────────
const PageWrapper = styled(Box)(({ theme }) => ({
  minHeight: "calc(100vh - 70px)",
  backgroundColor: theme.palette.background.default,
  paddingTop: 16,
  paddingBottom: 40,
}));

const SidebarWrapper = styled(Box)(({ theme }) => ({
  position: "sticky",
  top: 80,
  maxHeight: "calc(100vh - 110px)",
  overflowY: "auto",
  borderRight: `1px solid ${theme.palette.divider}`,
  paddingRight: 8,
  "&::-webkit-scrollbar": { width: 4 },
  "&::-webkit-scrollbar-thumb": { background: theme.palette.divider, borderRadius: 4 },
}));

const CodeBlock = styled(Box)(({ theme }) => ({
  position: "relative",
  backgroundColor: theme.palette.mode === "dark" ? "#1a1a2e" : "#0f172a",
  borderRadius: 8,
  padding: "16px 20px",
  fontFamily: "'Fira Code', 'Cascadia Code', monospace",
  fontSize: 13,
  lineHeight: 1.7,
  overflowX: "auto",
  marginTop: 12,
  marginBottom: 12,
  "& .comment": { color: "#64748b" },
  "& .keyword": { color: "#7dd3fc" },
  "& .string": { color: "#86efac" },
  "& .value": { color: "#fda4af" },
  "& .key": { color: "#c4b5fd" },
}));

const SectionCard = styled(Card)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "none",
  borderRadius: 10,
  padding: "28px 32px",
  marginBottom: 24,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: 22,
  fontWeight: 700,
  color: theme.palette.text.primary,
  marginBottom: 8,
  display: "flex",
  alignItems: "center",
  gap: 10,
}));

const SubTitle = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginTop: 20,
  marginBottom: 8,
}));

const Prose = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.mode === "dark" ? "#b0b8c4" : "#4b5563",
  lineHeight: 1.8,
}));

const InlineCode = styled("code")(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#2d3748" : "#f1f5f9",
  color: theme.palette.mode === "dark" ? "#e2e8f0" : "#0f172a",
  padding: "2px 6px",
  borderRadius: 4,
  fontSize: 12.5,
  fontFamily: "monospace",
}));

const MethodBadge = styled(Chip)(() => ({
  fontWeight: 700,
  fontSize: 11,
  height: 22,
}));

// ─── Copy button ──────────────────────────────────────────────────────────────
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <Tooltip title={copied ? "Copied!" : "Copy"}>
      <IconButton size="small" onClick={handle} sx={{ position: "absolute", top: 8, right: 8, color: "#94a3b8" }}>
        {copied ? <IconCheck size={15} /> : <IconCopy size={15} />}
      </IconButton>
    </Tooltip>
  );
}

// ─── Nav sections ─────────────────────────────────────────────────────────────
const NAV = [
  { id: "overview",      label: "Overview",              icon: <IconBook size={16} /> },
  { id: "authentication",label: "Authentication",         icon: <IconShieldLock size={16} /> },
  { id: "tokens",        label: "Managing API Tokens",   icon: <IconKey size={16} /> },
  { id: "scopes",        label: "Scopes Reference",      icon: <IconShieldLock size={16} /> },
  { id: "graphql",       label: "GraphQL Endpoint",      icon: <IconBrandGraphql size={16} /> },
  { id: "projects",      label: "Projects API",           icon: <IconApi size={16} /> },
  { id: "content",       label: "Content Queries API",   icon: <IconApi size={16} /> },
  { id: "blog-auto",     label: "Automated Blog (PAT)",  icon: <IconRocket size={16} /> },
  { id: "blog",          label: "Manual Blog Flow",      icon: <IconApi size={16} /> },
  { id: "schedule",      label: "Scheduling & Publishing",icon: <IconApi size={16} /> },
  { id: "seo",           label: "SEO & Audit API",       icon: <IconApi size={16} /> },
  { id: "examples",      label: "Full Code Examples",    icon: <IconCode size={16} /> },
];

// ─── Scope table data ─────────────────────────────────────────────────────────
const SCOPES = [
  {
    scope: "CONTENT_READ",
    category: "Content",
    color: "primary",
    description: "List and read content queries",
    operations: ["getContentQueriesByUserId", "getContentQueryById"],
  },
  {
    scope: "CONTENT_WRITE",
    category: "Content",
    color: "primary",
    description: "Create, update and delete content queries; save article HTML; analyse & score blog",
    operations: ["createContentQuery", "deleteContentQueryById", "updateBlogDescriptionById", "analyseAndUpdateBlog", "updateContentQueryStatus"],
  },
  {
    scope: "BLOG_GENERATE",
    category: "Content",
    color: "success",
    description: "Single-shot automated blog generation + individual outline, article, title, meta description and internal link operations",
    operations: ["automateGenerateBlog", "generateOutline", "generateArticleFromOutline", "generateTitleByQueryId", "generateMetaDescriptionByQueryId", "generateInternalLinks", "repurposeContent"],
  },
  {
    scope: "BLOG_SCHEDULE",
    category: "Publishing",
    color: "warning",
    description: "Schedule, publish, reschedule and cancel posts on WordPress / Shopify / Magento",
    operations: ["schedulePost", "publishScheduledPost", "reschedulePost", "cancelScheduledPost", "scheduledPosts"],
  },
  {
    scope: "SEO_READ",
    category: "SEO",
    color: "info",
    description: "Read site audit results and AI recommendations",
    operations: ["GetSiteAudit", "getAiRecommendations", "getActionPlan", "generateSchemaMarkup"],
  },
  {
    scope: "SEO_WRITE",
    category: "SEO",
    color: "info",
    description: "Run site audits, generate fix recommendations and action plans",
    operations: ["CreateSiteAudit", "generateFixRecommendations", "generateActionPlan", "markRecommendationApplied"],
  },
  {
    scope: "KEYWORD_RESEARCH",
    category: "Keywords",
    color: "secondary",
    description: "Create keyword tags, research keywords, select competitors",
    operations: ["createKeywordTag", "getKeywordTag", "getKeywordTitle", "updateSelectedCompetitorsAddKeywords"],
  },
  {
    scope: "KEYWORD_RANKING",
    category: "Keywords",
    color: "secondary",
    description: "Track and add keyword rankings",
    operations: ["getKeywordRanking", "addKeywordForRanking"],
  },
  {
    scope: "AI_CHAT",
    category: "AI",
    color: "error",
    description: "Send messages to AI chat, read and clear chat history",
    operations: ["sendChatMessage", "getChatHistory", "clearChatHistory"],
  },
  {
    scope: "AI_RECOMMENDATIONS",
    category: "AI",
    color: "error",
    description: "Generate and retrieve AI-powered SEO recommendations",
    operations: ["generateFixRecommendations", "getAiRecommendations", "generateActionPlan", "getActionPlan"],
  },
  {
    scope: "AI_AGENT",
    category: "AI",
    color: "error",
    description: "Control the SEO Autopilot Agent — run, configure and view task logs",
    operations: ["runAutopilotAgent", "getAgentConfig", "updateAgentConfig", "getAgentTaskLogs", "getAgentSummary"],
  },
  {
    scope: "PROJECT_READ",
    category: "Projects",
    color: "default",
    description: "Read connected website projects",
    operations: ["getScrapyData", "getUserWebsite"],
  },
  {
    scope: "PROJECT_WRITE",
    category: "Projects",
    color: "default",
    description: "Create, update and delete website projects",
    operations: ["createProject", "updateProject", "deleteProject"],
  },
  {
    scope: "ANALYTICS_READ",
    category: "Analytics",
    color: "default",
    description: "Read GSC/GA4 keyword and traffic analytics",
    operations: ["keywords", "analyzeKeywords"],
  },
  {
    scope: "UPTIME_READ",
    category: "Monitoring",
    color: "default",
    description: "Read uptime status and downtime history graphs",
    operations: ["getUptimeStatus", "getDownTimeGraphValue"],
  },
];

// ─── Collapsible code section ─────────────────────────────────────────────────
function CollapsibleCode({ title, code }) {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ mb: 1 }}>
      <Box
        onClick={() => setOpen(o => !o)}
        sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer", userSelect: "none", mb: 0.5 }}
      >
        {open ? <IconChevronDown size={15} /> : <IconChevronRight size={15} />}
        <Typography variant="caption" sx={{ fontWeight: 600, fontFamily: "monospace", fontSize: 13 }}>
          {title}
        </Typography>
      </Box>
      <Collapse in={open}>
        <CodeBlock>
          <CopyButton text={code} />
          <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", color: "#e2e8f0" }}>{code}</pre>
        </CodeBlock>
      </Collapse>
    </Box>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function DocumentationPage() {
  const theme = useTheme();
  const [activeSection, setActiveSection] = useState("overview");

  // ── Scroll-spy: update activeSection as user scrolls ──────────────────────
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the intersecting entry closest to the top of the viewport
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          const id = visible[0].target.id.replace("doc-", "");
          setActiveSection(id);
        }
      },
      {
        // -80px top offset accounts for the sticky header
        // -50% bottom margin means a section activates once its top
        // is in the upper half of the viewport
        rootMargin: "-80px 0px -50% 0px",
        threshold: 0,
      }
    );

    NAV.forEach(({ id }) => {
      const el = document.getElementById(`doc-${id}`);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    setActiveSection(id);
    const el = document.getElementById(`doc-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const GQL_ENDPOINT = "http://localhost:4000/graphql";

  return (
    <PageWrapper>
      <Container maxWidth="xl">
        <Breadcrumb title="API Documentation" items={BCrumb} />
        <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>

          {/* ── Sidebar ── */}
          <SidebarWrapper sx={{ width: 220, flexShrink: 0, display: { xs: "none", md: "block" } }}>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "text.secondary", px: 2, display: "block", mb: 1 }}>
              Contents
            </Typography>
            <List dense disablePadding>
              {NAV.map(n => (
                <ListItemButton
                  key={n.id}
                  selected={activeSection === n.id}
                  onClick={() => scrollTo(n.id)}
                  sx={{
                    borderRadius: 1.5,
                    mb: 0.5,
                    py: 0.8,
                    "&.Mui-selected": {
                      backgroundColor: theme.palette.primary.main + "18",
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  <Box sx={{ mr: 1, opacity: 0.7 }}>{n.icon}</Box>
                  <ListItemText primary={n.label} primaryTypographyProps={{ fontSize: 13, fontWeight: activeSection === n.id ? 700 : 400 }} />
                </ListItemButton>
              ))}
            </List>
          </SidebarWrapper>

          {/* ── Content ── */}
          <Box sx={{ flex: 1, minWidth: 0 }}>

            {/* OVERVIEW */}
            <SectionCard id="doc-overview">
              <SectionTitle><IconBook size={22} /> Overview</SectionTitle>
              <Prose>
                The HotspotSEO API is a <strong>GraphQL API</strong> that lets you automate every feature available
                in the dashboard — content creation, blog generation, SEO audits, keyword research, scheduling, and more.
                All requests are authenticated with a <strong>Personal Access Token (PAT)</strong> passed as a Bearer token.
              </Prose>
              <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                {[
                  { label: "Protocol", value: "GraphQL over HTTP POST" },
                  { label: "Endpoint", value: GQL_ENDPOINT },
                  { label: "Auth", value: "Bearer token (PAT or JWT)" },
                  { label: "Format", value: "application/json" },
                ].map(({ label, value }) => (
                  <Box key={label} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, px: 2, py: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 700 }}>{label}</Typography>
                    <InlineCode>{value}</InlineCode>
                  </Box>
                ))}
              </Box>
            </SectionCard>

            {/* AUTHENTICATION */}
            <SectionCard id="doc-authentication">
              <SectionTitle><IconShieldLock size={22} /> Authentication</SectionTitle>
              <Prose>
                Every API request must include an <InlineCode>Authorization</InlineCode> header with your PAT as a Bearer token.
                JWTs from the web app are also accepted for same-origin browser requests.
              </Prose>
              <SubTitle>Request header</SubTitle>
              <CodeBlock>
                <CopyButton text={`Authorization: Bearer hsp_<your_token_here>`} />
                <pre style={{ margin: 0, color: "#e2e8f0" }}><span className="comment"># Add this header to every request</span>{"\n"}<span className="key">Authorization</span>: <span className="string">Bearer hsp_&lt;your_token_here&gt;</span></pre>
              </CodeBlock>

              <SubTitle>cURL example</SubTitle>
              <CodeBlock>
                <CopyButton text={`curl -X POST ${GQL_ENDPOINT} \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer hsp_<your_token>" \\
  -d '{"query":"{ getContentQueriesByUserId { _id queryName status } }"}'`} />
                <pre style={{ margin: 0, color: "#e2e8f0" }}>{`curl -X POST ${GQL_ENDPOINT} \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer hsp_<your_token>" \\
  -d '{"query":"{ getContentQueriesByUserId { _id queryName status } }"}'`}</pre>
              </CodeBlock>

              <Alert severity="warning" sx={{ mt: 2, fontSize: 13 }}>
                Tokens are hashed with SHA-256 before storage. The raw token is shown <strong>only once</strong> when created — copy it immediately. If lost, revoke and create a new one.
              </Alert>
            </SectionCard>

            {/* MANAGING TOKENS */}
            <SectionCard id="doc-tokens">
              <SectionTitle><IconKey size={22} /> Managing API Tokens</SectionTitle>
              <Prose>
                Go to{" "}
                <Link
                  to="/account-settings?tab=3"
                  style={{ color: "inherit", fontWeight: 700, textDecoration: "underline" }}
                >
                  Account Settings → API Tokens
                </Link>{" "}
                to create, view and revoke tokens.
                Each token has a name, optional expiry date, and a set of scopes that limit what it can do.
              </Prose>

              <SubTitle>Token format</SubTitle>
              <Prose>
                Every token starts with the prefix <InlineCode>hsp_</InlineCode> followed by 64 hex characters (256 bits of entropy).
                The prefix shown in the table is the first 10 characters of the raw token.
              </Prose>

              <SubTitle>Token lifecycle</SubTitle>
              <Box component="ol" sx={{ pl: 3, mt: 1 }}>
                {[
                  <><Link to="/account-settings?tab=3" style={{ color: "inherit", fontWeight: 600, textDecoration: "underline" }}>Create a token in Account Settings → API Tokens</Link></>,
                  "Copy the raw token — it is shown only once",
                  "Use it as a Bearer token in API requests",
                  "Each successful request updates the Last used timestamp",
                  "Revoke a token at any time from the tokens table",
                  "Expired tokens (shown in red) are automatically rejected",
                ].map((step, i) => (
                  <Typography component="li" key={i} sx={{ fontSize: 14, color: "text.secondary", mb: 0.5 }}>
                    {step}
                  </Typography>
                ))}
              </Box>

              <SubTitle>Create token — GraphQL mutation</SubTitle>
              <CodeBlock>
                <CopyButton text={`mutation {
  generateApiToken(data: {
    name: "My Automation"
    scopes: ["CONTENT_READ", "CONTENT_WRITE", "BLOG_GENERATE"]
    expiresAt: "2026-12-31T23:59:59.999Z"  # optional
  }) {
    id
    prefix
    rawToken   # save this — shown only once
    scopes
    expiresAt
  }
}`} />
                <pre style={{ margin: 0, color: "#e2e8f0" }}>{`mutation {
  generateApiToken(data: {
    name: "My Automation"
    scopes: ["CONTENT_READ", "CONTENT_WRITE", "BLOG_GENERATE"]
    expiresAt: "2026-12-31T23:59:59.999Z"  # optional
  }) {
    id
    prefix
    rawToken   # save this — shown only once
    scopes
    expiresAt
  }
}`}</pre>
              </CodeBlock>

              <SubTitle>Revoke token</SubTitle>
              <CodeBlock>
                <CopyButton text={`mutation {
  revokeApiToken(tokenId: "<token-id>")
}`} />
                <pre style={{ margin: 0, color: "#e2e8f0" }}>{`mutation {
  revokeApiToken(tokenId: "<token-id>")
}`}</pre>
              </CodeBlock>
            </SectionCard>

            {/* SCOPES */}
            <SectionCard id="doc-scopes">
              <SectionTitle><IconShieldLock size={22} /> Scopes Reference</SectionTitle>
              <Prose>
                Scopes restrict what a token can do. Always grant the minimum set of scopes needed.
                JWT sessions (browser login) bypass scope enforcement and have full access.
              </Prose>
              <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ "& th": { fontWeight: 700, fontSize: 12, color: "text.secondary", backgroundColor: theme.palette.mode === "dark" ? "#1e1e2e" : "#f8fafc" } }}>
                      <TableCell>Scope</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Operations unlocked</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {SCOPES.map(row => (
                      <TableRow key={row.scope} sx={{ verticalAlign: "top", "&:last-child td": { border: 0 } }}>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Chip label={row.scope} size="small" color={row.color} variant="outlined" sx={{ fontFamily: "monospace", fontSize: 11, height: 22 }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>{row.category}</TableCell>
                        <TableCell sx={{ fontSize: 12, maxWidth: 220 }}>{row.description}</TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {row.operations.map(op => (
                              <InlineCode key={op} sx={{ fontSize: 11 }}>{op}</InlineCode>
                            ))}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </SectionCard>

            {/* GRAPHQL */}
            <SectionCard id="doc-graphql">
              <SectionTitle><IconBrandGraphql size={22} /> GraphQL Endpoint</SectionTitle>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                <MethodBadge label="POST" color="primary" size="small" />
                <InlineCode sx={{ fontSize: 14 }}>{GQL_ENDPOINT}</InlineCode>
              </Box>
              <Prose>
                All operations — queries and mutations — are sent as HTTP POST to the same endpoint.
                The body is a JSON object with a <InlineCode>query</InlineCode> string and an optional <InlineCode>variables</InlineCode> object.
              </Prose>

              <SubTitle>Request body shape</SubTitle>
              <CodeBlock>
                <CopyButton text={`{
  "query": "mutation { ... }",
  "variables": { "key": "value" },
  "operationName": "MyOperation"  // optional
}`} />
                <pre style={{ margin: 0, color: "#e2e8f0" }}>{`{
  "query": "mutation { ... }",
  "variables": { "key": "value" },
  "operationName": "MyOperation"  // optional
}`}</pre>
              </CodeBlock>

              <SubTitle>Node.js request helper</SubTitle>
              <CodeBlock>
                <CopyButton text={`const http = require('http');

async function gql(query, variables = {}) {
  const body = JSON.stringify({ query, variables });
  return new Promise((resolve, reject) => {
    const req = http.request({
      hostname: 'localhost', port: 4000, path: '/graphql', method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer hsp_<your_token>',
        'Content-Length': Buffer.byteLength(body),
      },
    }, res => {
      let data = '';
      res.on('data', c => data += c);
      res.on('end', () => resolve(JSON.parse(data).data));
    });
    req.on('error', reject);
    req.write(body);
    req.end();
  });
}`} />
                <pre style={{ margin: 0, color: "#e2e8f0" }}>{`const http = require('http');

async function gql(query, variables = {}) {
  const body = JSON.stringify({ query, variables });
  return new Promise((resolve, reject) => {
    const req = http.request({
      hostname: 'localhost', port: 4000, path: '/graphql', method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer hsp_<your_token>',
        'Content-Length': Buffer.byteLength(body),
      },
    }, res => {
      let data = '';
      res.on('data', c => data += c);
      res.on('end', () => resolve(JSON.parse(data).data));
    });
    req.on('error', reject);
    req.write(body);
    req.end();
  });
}`}</pre>
              </CodeBlock>

              <Alert severity="info" sx={{ mt: 2, fontSize: 13 }}>
                The GraphQL Playground / Apollo Sandbox is available at <InlineCode>{GQL_ENDPOINT}</InlineCode> in your browser when the server is running. Use it to explore the full schema interactively.
              </Alert>
            </SectionCard>

            {/* PROJECTS */}
            <SectionCard id="doc-projects">
              <SectionTitle><IconApi size={22} /> Projects API</SectionTitle>
              <Prose>
                Read all website projects connected to your account. Use the returned <InlineCode>id</InlineCode> values
                as <InlineCode>projectId</InlineCode> inputs in scheduling, site audit, and analytics calls.
              </Prose>

              <Alert severity="success" sx={{ mt: 2, mb: 2, fontSize: 13 }}>
                <strong>Required scope:</strong> <InlineCode>PROJECT_READ</InlineCode>
              </Alert>

              <SubTitle>List all projects</SubTitle>
              <CodeBlock>
                <CopyButton text={`query {
  getUserWebsite {
    id
    projectName
    website
    isVerified
    verificationType
    userId
  }
}`} />
                <pre style={{ margin: 0, color: "#e2e8f0" }}>{`query {
  getUserWebsite {
    id
    projectName
    website
    isVerified
    verificationType
    userId
  }
}`}</pre>
              </CodeBlock>

              <SubTitle>curl example</SubTitle>
              <CodeBlock>
                <CopyButton text={`curl -X POST http://localhost:4000/graphql \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer hsp_<your_token>" \\
  -d '{"query": "query { getUserWebsite { id projectName website isVerified } }"}'`} />
                <pre style={{ margin: 0, color: "#e2e8f0" }}>{`curl -X POST http://localhost:4000/graphql \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer hsp_<your_token>" \\
  -d '{"query": "query { getUserWebsite { id projectName website isVerified } }"}'`}</pre>
              </CodeBlock>

              <SubTitle>Response fields</SubTitle>
              <TableContainer sx={{ mt: 1, mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ "& th": { fontWeight: 700, fontSize: 12, color: "text.secondary", backgroundColor: theme.palette.mode === "dark" ? "#1e1e2e" : "#f8fafc" } }}>
                      <TableCell>Field</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      { field: "id", type: "String", desc: "Project ID — use as projectId in other calls" },
                      { field: "projectName", type: "String", desc: "Human-readable project label" },
                      { field: "website", type: "String", desc: "Full website URL (e.g. https://example.com)" },
                      { field: "isVerified", type: "Boolean", desc: "Whether ownership has been verified" },
                      { field: "verificationType", type: "String", desc: "Verification method: search_console | meta_data | dns | html_file | html_tag" },
                      { field: "userId", type: "String", desc: "Owner user ID" },
                    ].map(row => (
                      <TableRow key={row.field} sx={{ "&:last-child td": { border: 0 } }}>
                        <TableCell><InlineCode sx={{ fontSize: 12 }}>{row.field}</InlineCode></TableCell>
                        <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>{row.type}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{row.desc}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </SectionCard>

            {/* CONTENT QUERIES */}
            <SectionCard id="doc-content">
              <SectionTitle><IconApi size={22} /> Content Queries API</SectionTitle>
              <Prose>
                A <strong>Content Query</strong> is the central object for everything in AI Content &amp; SEO Hub.
                It holds the topic, competitors, keywords, generated article HTML, SEO scores and publishing status.
              </Prose>

              <SubTitle>List all content queries</SubTitle>
              <Prose>Requires: <Chip label="CONTENT_READ" size="small" sx={{ fontSize: 11, height: 20, ml: 0.5 }} /></Prose>
              <CodeBlock>
                <CopyButton text={`query {
  getContentQueriesByUserId {
    _id
    queryName
    status        # "In Progress" | "Done" | "Deleted"
    content_score
    wordsCount
    title
    metaDescription
    createdAt
  }
}`} />
                <pre style={{ margin: 0, color: "#e2e8f0" }}>{`query {
  getContentQueriesByUserId {
    _id
    queryName
    status        # "In Progress" | "Done" | "Deleted"
    content_score
    wordsCount
    title
    metaDescription
    createdAt
  }
}`}</pre>
              </CodeBlock>

              <SubTitle>Get a single content query</SubTitle>
              <CodeBlock>
                <CopyButton text={`query {
  getContentQueryById(queryId: "<id>") {
    _id
    queryName
    status
    title
    metaDescription
    htmlBlogDescription
    content_score
    competitors { _id url title googleRank isSelected }
    plannerKeywords { name monthlySearches }
  }
}`} />
                <pre style={{ margin: 0, color: "#e2e8f0" }}>{`query {
  getContentQueryById(queryId: "<id>") {
    _id
    queryName
    status
    title
    metaDescription
    htmlBlogDescription
    content_score
    competitors { _id url title googleRank isSelected }
    plannerKeywords { name monthlySearches }
  }
}`}</pre>
              </CodeBlock>

              <SubTitle>Update content query status</SubTitle>
              <Prose>Requires: <Chip label="CONTENT_WRITE" size="small" sx={{ fontSize: 11, height: 20, ml: 0.5 }} /></Prose>
              <CodeBlock>
                <CopyButton text={`mutation {
  updateContentQueryStatus(
    queryId: "<id>"
    status: "Done"   # "In Progress" | "Done" | "Deleted"
  ) {
    _id
    status
  }
}`} />
                <pre style={{ margin: 0, color: "#e2e8f0" }}>{`mutation {
  updateContentQueryStatus(
    queryId: "<id>"
    status: "Done"   # "In Progress" | "Done" | "Deleted"
  ) {
    _id
    status
  }
}`}</pre>
              </CodeBlock>
            </SectionCard>

            {/* AUTOMATED BLOG (PAT) */}
            <SectionCard id="doc-blog-auto">
              <SectionTitle><IconRocket size={22} /> Automated Blog Generation (PAT)</SectionTitle>
              <Prose>
                <InlineCode>automateGenerateBlog</InlineCode> is an <strong>async single-shot mutation</strong> that runs the entire
                blog pipeline server-side — from SERP competitor fetch through keyword research, title, meta description,
                outline, article HTML, and scoring. The mutation returns <strong>immediately</strong> after the first step with
                status <InlineCode>Processing</InlineCode>. The remaining steps run in the background.
                Poll <InlineCode>getContentQueryById</InlineCode> to track progress. Terminal statuses:
                <InlineCode>Generated</InlineCode> (success — review and mark Done) or <InlineCode>Failed</InlineCode> (error).
              </Prose>

              <Alert severity="success" sx={{ mt: 2, mb: 2, fontSize: 13 }}>
                <strong>Required scope:</strong> <InlineCode>BLOG_GENERATE</InlineCode> — this single scope covers the full pipeline.
                JWT users (browser sessions) can also call this without any scope restrictions.
              </Alert>

              <SubTitle>Input parameters</SubTitle>
              <TableContainer sx={{ mt: 1, mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ "& th": { fontWeight: 700, fontSize: 12, color: "text.secondary", backgroundColor: theme.palette.mode === "dark" ? "#1e1e2e" : "#f8fafc" } }}>
                      <TableCell>Field</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Default</TableCell>
                      <TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      { field: "query", type: "String!", def: "—", desc: "Topic or keyword for the blog (required)" },
                      { field: "locations", type: "[String]", def: '["us"]', desc: 'ISO country codes for SERP + keyword planner, e.g. ["in"], ["us","gb"]' },
                      { field: "wordCount", type: "Int", def: "3000", desc: "Target article word count (recommended: 2000–5000)" },
                      { field: "includeImages", type: "Boolean", def: "false", desc: "Fetch and embed Unsplash stock images per section" },
                      { field: "includeToc", type: "Boolean", def: "true", desc: "Prepend a Table of Contents section to the article" },
                      { field: "includeFaq", type: "Boolean", def: "true", desc: "Include an AI-generated FAQ section in the article" },
                      { field: "requirements", type: "String", def: "null", desc: "Optional custom instructions passed to outline and article generation (e.g. tone, focus areas, things to avoid)" },
                    ].map(row => (
                      <TableRow key={row.field} sx={{ verticalAlign: "top", "&:last-child td": { border: 0 } }}>
                        <TableCell><InlineCode sx={{ fontSize: 12 }}>{row.field}</InlineCode></TableCell>
                        <TableCell sx={{ fontSize: 12, color: "text.secondary", whiteSpace: "nowrap" }}>{row.type}</TableCell>
                        <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>{row.def}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{row.desc}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Alert severity="info" sx={{ mb: 2, fontSize: 13 }}>
                <strong>Async — returns immediately.</strong> The mutation completes step 1 (SERP fetch) and returns the
                <InlineCode>ContentQuery</InlineCode> with <InlineCode>status: "Processing"</InlineCode> right away —
                no gateway timeout risk. Poll <InlineCode>getContentQueryById(_id)</InlineCode> until status becomes
                <InlineCode>Generated</InlineCode> (success) or <InlineCode>Failed</InlineCode> (error).
                When satisfied, call <InlineCode>updateContentQueryStatus</InlineCode> with <InlineCode>status: "Done"</InlineCode> to publish it.
              </Alert>

              <SubTitle>Mutation</SubTitle>
              <CodeBlock>
                <CopyButton text={`mutation AutoBlog($data: AutoBlogInput!) {
  automateGenerateBlog(data: $data) {
    _id        # use this to poll progress
    queryName
    status     # returns "Processing" immediately
    createdAt
  }
}
# Poll getContentQueryById(_id) until status = "Generated" | "Failed"
# Then call updateContentQueryStatus(queryId, "Done") to publish`} />
                <pre style={{ margin: 0, color: "#e2e8f0" }}>{`mutation AutoBlog($data: AutoBlogInput!) {
  automateGenerateBlog(data: $data) {
    _id        # use this to poll progress
    queryName
    status     # returns "Processing" immediately
    createdAt
  }
}
# Poll getContentQueryById(_id) until status = "Generated" | "Failed"
# Then call updateContentQueryStatus(queryId, "Done") to publish`}</pre>
              </CodeBlock>

              <SubTitle>Minimal example (curl)</SubTitle>
              <CodeBlock>
                <CopyButton text={`curl -X POST http://localhost:4000/graphql \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer hsp_<your_token>" \\
  -d '{
    "query": "mutation($d: AutoBlogInput!) { automateGenerateBlog(data: $d) { _id title content_score status } }",
    "variables": {
      "d": {
        "query": "AI SEO tools 2026",
        "locations": ["us"],
        "wordCount": 3000,
        "includeImages": false,
        "includeToc": true,
        "includeFaq": true
      }
    }
  }'`} />
                <pre style={{ margin: 0, color: "#e2e8f0" }}>{`curl -X POST http://localhost:4000/graphql \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer hsp_<your_token>" \\
  -d '{
    "query": "mutation($d: AutoBlogInput!) { automateGenerateBlog(data: $d) { _id title content_score status } }",
    "variables": {
      "d": {
        "query": "AI SEO tools 2026",
        "locations": ["us"],
        "wordCount": 3000,
        "includeImages": false,
        "includeToc": true,
        "includeFaq": true
      }
    }
  }'`}</pre>
              </CodeBlock>

              <SubTitle>Node.js example with requirements</SubTitle>
              <CodeBlock>
                <CopyButton text={`// 1. Trigger — returns immediately with status "Processing"
const { automateGenerateBlog: job } = await gql(
  \`mutation($d: AutoBlogInput!) {
    automateGenerateBlog(data: $d) { _id queryName status }
  }\`,
  {
    d: {
      query: "best CRM software for startups 2026",
      locations: ["us"],
      wordCount: 3000,
      includeImages: false,
      includeToc: true,
      includeFaq: true,
      requirements: "Focus on pricing comparisons. Target SaaS founders. Avoid enterprise tools."
    }
  }
);
console.log('queryId:', job._id, '| status:', job.status); // "Processing"

// 2. Poll until "Generated" or "Failed"
let blog;
do {
  await new Promise(r => setTimeout(r, 10000));
  const { getContentQueryById: q } = await gql(
    \`query($id:String!){getContentQueryById(queryId:$id){_id status content_score wordsCount title}}\`,
    { id: job._id }
  );
  blog = q;
  console.log('status:', blog.status);
} while (blog.status === 'Processing');

if (blog.status === 'Failed') throw new Error('Generation failed');
console.log('Generated! score:', blog.content_score, 'words:', blog.wordsCount);

// 3. Mark Done to publish in UI
await gql(
  \`mutation{updateContentQueryStatus(queryId:"\${blog._id}",status:"Done"){_id status}}\`
);`} />
                <pre style={{ margin: 0, color: "#e2e8f0" }}>{`// 1. Trigger — returns immediately with status "Processing"
const { automateGenerateBlog: job } = await gql(
  \`mutation($d: AutoBlogInput!) {
    automateGenerateBlog(data: $d) { _id queryName status }
  }\`,
  {
    d: {
      query: "best CRM software for startups 2026",
      locations: ["us"],
      wordCount: 3000,
      includeImages: false,
      includeToc: true,
      includeFaq: true,
      requirements: "Focus on pricing comparisons. Target SaaS founders. Avoid enterprise tools."
    }
  }
);
console.log('queryId:', job._id, '| status:', job.status); // "Processing"

// 2. Poll until "Generated" or "Failed"
let blog;
do {
  await new Promise(r => setTimeout(r, 10000));
  const { getContentQueryById: q } = await gql(
    \`query($id:String!){getContentQueryById(queryId:$id){_id status content_score wordsCount title}}\`,
    { id: job._id }
  );
  blog = q;
  console.log('status:', blog.status);
} while (blog.status === 'Processing');

if (blog.status === 'Failed') throw new Error('Generation failed');
console.log('Generated! score:', blog.content_score, 'words:', blog.wordsCount);

// 3. Mark Done to publish in UI
await gql(
  \`mutation{updateContentQueryStatus(queryId:"\${blog._id}",status:"Done"){_id status}}\`
);`}</pre>
              </CodeBlock>
            </SectionCard>

            {/* MANUAL BLOG GENERATION FLOW */}
            <SectionCard id="doc-blog">
              <SectionTitle><IconApi size={22} /> Manual Blog Flow (Step-by-step)</SectionTitle>
              <Alert severity="info" sx={{ mb: 2, fontSize: 13 }}>
                Use these individual mutations when you need fine-grained control — e.g. to review and edit the outline before generating the article.
                For fully automated generation use <InlineCode>automateGenerateBlog</InlineCode> above.
                All 9 steps must be completed in order for the blog to appear in the UI.
              </Alert>

              {[
                {
                  step: 1,
                  name: "createContentQuery",
                  scope: "CONTENT_WRITE",
                  desc: "Creates the content query record and automatically fetches ~25 SERP competitors from Google.",
                  code: `mutation {
  createContentQuery(queryName: "How AI is Revolutionizing SEO in 2026") {
    _id
    queryName
    status
    competitors { _id url title googleRank }
  }
}`,
                },
                {
                  step: 2,
                  name: "updateSelectedCompetitorsAddKeywords",
                  scope: "KEYWORD_RESEARCH",
                  desc: "Select up to 10 competitors and set target location (ISO country code). This triggers keyword research via Google Keyword Planner.",
                  code: `mutation {
  updateSelectedCompetitorsAddKeywords(data: {
    queryId: "<id>"
    competitorIds: ["<comp-id-1>", "<comp-id-2>", ...]  # up to 10
    locations: ["in"]  # ISO code: us, in, gb, au, ca ...
    loc_ids: [2356]    # optional Google Ads location ID
  }) {
    _id
    selectedByUser
    status
  }
}`,
                },
                {
                  step: 3,
                  name: "generateTitleByQueryId",
                  scope: "BLOG_GENERATE",
                  desc: "AI generates an SEO-optimised title based on competitor titles and your topic.",
                  code: `query {
  generateTitleByQueryId(queryId: "<id>")
}
# Returns: String — use this as the title for all subsequent steps`,
                },
                {
                  step: 4,
                  name: "generateMetaDescriptionByQueryId",
                  scope: "BLOG_GENERATE",
                  desc: "AI generates a 150–160 character meta description optimised for CTR.",
                  code: `query {
  generateMetaDescriptionByQueryId(queryId: "<id>")
}
# Returns: String — pass this to analyseAndUpdateBlog to persist it`,
                },
                {
                  step: 5,
                  name: "generateOutline",
                  scope: "BLOG_GENERATE",
                  desc: "AI generates a structured outline with H2/H3 headings. Returns JSON.",
                  code: `mutation {
  generateOutline(data: {
    queryId: "<id>"
    title: "<generated title>"
    BlogDescription: "<meta description>"
    wordCount: 2000
  })
}
# Returns JSON: { outline: [{ h2: "...", h3: ["...", "..."] }, ...] }`,
                },
                {
                  step: 6,
                  name: "generateArticleFromOutline",
                  scope: "BLOG_GENERATE",
                  desc: "AI writes the full HTML article. Headings must be prefixed with h2: or h3:. Pass includeImages: \"yes\" to embed stock images.",
                  code: `mutation {
  generateArticleFromOutline(data: {
    queryId: "<id>"
    title: "<title>"
    BlogDescription: "<meta description>"
    outlineHeadings: [
      "h2:Understanding AI-Driven SEO in 2026"
      "h3:How AI Enhances Keyword Research"
      "h3:Predictive Analytics in SEO"
      "h2:Common Mistakes to Avoid"
      "h2:Key Takeaways"
      "h2:FAQ"
      "h2:Conclusion"
    ]
    includeImages: "yes"  # "yes" | "no" — embeds Unsplash/Pexels images
    wordCount: 2000
  })
}
# Returns: String (HTML)`,
                },
                {
                  step: 7,
                  name: "updateBlogDescriptionById",
                  scope: "CONTENT_WRITE",
                  desc: "Saves the generated HTML to the content query record.",
                  code: `mutation {
  updateBlogDescriptionById(data: {
    queryId: "<id>"
    htmlDescription: "<full HTML string>"
  }) {
    _id
    status
  }
}`,
                },
                {
                  step: 8,
                  name: "analyseAndUpdateBlog",
                  scope: "CONTENT_WRITE",
                  desc: "Runs an AI SEO analysis, calculates the content score (0–100), and persists title + meta description to the DB.",
                  code: `mutation {
  analyseAndUpdateBlog(data: {
    queryId: "<id>"
    title: "<title>"
    html: "<full HTML string>"
    metaDescription: "<meta description>"  # optional — saves it to DB
  }) {
    _id
    content_score
    title
    metaDescription
    status
  }
}`,
                },
                {
                  step: 9,
                  name: "updateContentQueryStatus",
                  scope: "CONTENT_WRITE",
                  desc: "Marks the content query as Done — makes it appear in the \"Done\" tab of AI Content & SEO Hub.",
                  code: `mutation {
  updateContentQueryStatus(queryId: "<id>", status: "Done") {
    _id
    status
    queryName
  }
}`,
                },
              ].map(({ step, name, scope, desc, code }) => (
                <Box key={step} sx={{ mb: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
                    <Box sx={{ width: 26, height: 26, borderRadius: "50%", bgcolor: "primary.main", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>{step}</Typography>
                    </Box>
                    <InlineCode sx={{ fontSize: 14 }}>{name}</InlineCode>
                    <Chip label={scope} size="small" variant="outlined" sx={{ fontSize: 11, height: 20 }} />
                  </Box>
                  <Prose sx={{ ml: 4.5, mb: 0.5 }}>{desc}</Prose>
                  <Box sx={{ ml: 4.5 }}>
                    <CollapsibleCode title={`Show ${name} example`} code={code} />
                  </Box>
                  {step < 9 && <Divider sx={{ mt: 2 }} />}
                </Box>
              ))}
            </SectionCard>

            {/* SCHEDULING */}
            <SectionCard id="doc-schedule">
              <SectionTitle><IconApi size={22} /> Scheduling &amp; Publishing</SectionTitle>
              <Prose>
                Publish or schedule blog posts to connected WordPress, Shopify, or Magento sites.
                You need the connected project ID (<InlineCode>UserWebsiteVisit.id</InlineCode>) from <InlineCode>getScrapyData</InlineCode>.
              </Prose>
              <Prose sx={{ mt: 1 }}>Requires: <Chip label="BLOG_SCHEDULE" size="small" sx={{ fontSize: 11, height: 20, ml: 0.5 }} /> + <Chip label="PROJECT_READ" size="small" sx={{ fontSize: 11, height: 20, ml: 0.5 }} /></Prose>

              <SubTitle>Get connected projects (to find projectId)</SubTitle>
              <CodeBlock>
                <CopyButton text={`query {
  getScrapyData {
    id         # use this as projectId in schedulePost
    domain
    platform   # "wordpress" | "shopify" | "magento"
  }
}`} />
                <pre style={{ margin: 0, color: "#e2e8f0" }}>{`query {
  getScrapyData {
    id         # use this as projectId in schedulePost
    domain
    platform   # "wordpress" | "shopify" | "magento"
  }
}`}</pre>
              </CodeBlock>

              <SubTitle>Schedule a post</SubTitle>
              <CodeBlock>
                <CopyButton text={`mutation {
  schedulePost(data: {
    queryId: "<content-query-id>"
    projectId: "<UserWebsiteVisit-id>"
    scheduledAt: "2026-04-16T10:00:00.000Z"
  }) {
    id
    status      # "scheduled"
    scheduledAt
    platform
  }
}`} />
                <pre style={{ margin: 0, color: "#e2e8f0" }}>{`mutation {
  schedulePost(data: {
    queryId: "<content-query-id>"
    projectId: "<UserWebsiteVisit-id>"
    scheduledAt: "2026-04-16T10:00:00.000Z"
  }) {
    id
    status      # "scheduled"
    scheduledAt
    platform
  }
}`}</pre>
              </CodeBlock>

              <SubTitle>Publish immediately</SubTitle>
              <CodeBlock>
                <CopyButton text={`mutation {
  publishScheduledPost(scheduledPostId: "<id>") {
    id
    status   # "published"
    postUrl
  }
}`} />
                <pre style={{ margin: 0, color: "#e2e8f0" }}>{`mutation {
  publishScheduledPost(scheduledPostId: "<id>") {
    id
    status   # "published"
    postUrl
  }
}`}</pre>
              </CodeBlock>

              <SubTitle>List scheduled posts</SubTitle>
              <CodeBlock>
                <CopyButton text={`query {
  scheduledPosts {
    id
    status
    scheduledAt
    platform
    postUrl
    contentQuery { _id title }
  }
}`} />
                <pre style={{ margin: 0, color: "#e2e8f0" }}>{`query {
  scheduledPosts {
    id
    status
    scheduledAt
    platform
    postUrl
    contentQuery { _id title }
  }
}`}</pre>
              </CodeBlock>
            </SectionCard>

            {/* SEO & AUDIT */}
            <SectionCard id="doc-seo">
              <SectionTitle><IconApi size={22} /> SEO &amp; Audit API</SectionTitle>

              <SubTitle>Run a site audit</SubTitle>
              <Prose>Requires: <Chip label="SEO_WRITE" size="small" sx={{ fontSize: 11, height: 20, ml: 0.5 }} /></Prose>
              <CodeBlock>
                <CopyButton text={`mutation {
  CreateSiteAudit(domain: "https://example.com") {
    id
    domain
    status
    score
    issues { type severity message url }
  }
}`} />
                <pre style={{ margin: 0, color: "#e2e8f0" }}>{`mutation {
  CreateSiteAudit(domain: "https://example.com") {
    id
    domain
    status
    score
    issues { type severity message url }
  }
}`}</pre>
              </CodeBlock>

              <SubTitle>Get AI recommendations</SubTitle>
              <Prose>Requires: <Chip label="SEO_READ" size="small" sx={{ fontSize: 11, height: 20, ml: 0.5 }} /> or <Chip label="AI_RECOMMENDATIONS" size="small" sx={{ fontSize: 11, height: 20, ml: 0.5 }} /></Prose>
              <CodeBlock>
                <CopyButton text={`query {
  getAiRecommendations(auditId: "<id>") {
    id
    category
    priority
    recommendation
    applied
  }
}`} />
                <pre style={{ margin: 0, color: "#e2e8f0" }}>{`query {
  getAiRecommendations(auditId: "<id>") {
    id
    category
    priority
    recommendation
    applied
  }
}`}</pre>
              </CodeBlock>

              <SubTitle>Keyword research</SubTitle>
              <Prose>Requires: <Chip label="KEYWORD_RESEARCH" size="small" sx={{ fontSize: 11, height: 20, ml: 0.5 }} /></Prose>
              <CodeBlock>
                <CopyButton text={`query {
  getKeywordTitle(title: "AI SEO tools 2026") {
    keyword
    monthlySearches
    competition
    cpc
  }
}`} />
                <pre style={{ margin: 0, color: "#e2e8f0" }}>{`query {
  getKeywordTitle(title: "AI SEO tools 2026") {
    keyword
    monthlySearches
    competition
    cpc
  }
}`}</pre>
              </CodeBlock>
            </SectionCard>

            {/* FULL EXAMPLES */}
            <SectionCard id="doc-examples">
              <SectionTitle><IconCode size={22} /> Full Code Examples</SectionTitle>

              <SubTitle>Node.js — Automated blog generation (single call)</SubTitle>
              <Prose sx={{ mb: 1 }}>
                Requires token scopes: <Chip label="BLOG_GENERATE" size="small" sx={{ fontSize: 11, height: 20, mx: 0.5 }} />
              </Prose>
              <CodeBlock>
                <CopyButton text={`#!/usr/bin/env node
// automateGenerateBlog returns immediately (status "Processing").
// Poll until status = "Generated" | "Failed", then mark "Done".
const http = require('http');
const PAT = 'hsp_<your_token>';

async function gql(query, variables = {}) {
  const body = JSON.stringify({ query, variables });
  return new Promise((resolve, reject) => {
    const req = http.request(
      { hostname: 'localhost', port: 4000, path: '/graphql', method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': \`Bearer \${PAT}\`,
                   'Content-Length': Buffer.byteLength(body) } },
      res => {
        let d = '';
        res.on('data', c => d += c);
        res.on('end', () => {
          const p = JSON.parse(d);
          if (p.errors) throw new Error(p.errors[0].message);
          resolve(p.data);
        });
      }
    );
    req.on('error', reject);
    req.write(body); req.end();
  });
}

async function poll(queryId, intervalMs = 10000, timeoutMs = 300000) {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    await new Promise(r => setTimeout(r, intervalMs));
    const { getContentQueryById: q } = await gql(
      \`query($id: String!) { getContentQueryById(queryId: $id) {
        _id title metaDescription status
        content_score titleAccuracy headingsScore termsCoverage wordsCount
      }}\`,
      { id: queryId }
    );
    console.log(\`  Polling... status=\${q.status}\`);
    if (q.status === 'Generated' || q.status === 'Failed') return q;
  }
  throw new Error('Timed out waiting for blog generation');
}

async function main() {
  console.log('Triggering automated blog generation...');

  // Step 1: fire mutation — returns immediately with status "Processing"
  const { automateGenerateBlog: job } = await gql(
    \`mutation($d: AutoBlogInput!) {
      automateGenerateBlog(data: $d) { _id queryName status }
    }\`,
    {
      d: {
        query: "best CRM software for startups 2026",
        locations: ["us"],
        wordCount: 3000,
        includeImages: false,
        includeToc: true,
        includeFaq: true,
        requirements: "Focus on pricing comparisons. Target SaaS founders. Avoid enterprise tools."
      }
    }
  );
  console.log(\`  queryId: \${job._id}  status: \${job.status}\`);  // "Processing"

  // Step 2: poll until done
  const blog = await poll(job._id);

  if (blog.status === 'Failed') {
    console.error('Blog generation failed — check server logs for queryId:', job._id);
    process.exit(1);
  }

  console.log('Blog generated!');
  console.log('  ID:    ', blog._id);
  console.log('  Title: ', blog.title);
  console.log('  Score: ', blog.content_score);
  console.log('  Words: ', blog.wordsCount);

  // Step 3: mark Done so it appears in the UI "Done" tab
  await gql(
    \`mutation($id: String!, $s: String!) { updateContentQueryStatus(queryId: $id, status: $s) { _id status } }\`,
    { id: blog._id, s: 'Done' }
  );
  console.log('  Marked Done.');
}

main().catch(console.error);`} />
                <pre style={{ margin: 0, color: "#e2e8f0", fontSize: 12 }}>{`#!/usr/bin/env node
// automateGenerateBlog returns immediately (status "Processing").
// Poll until status = "Generated" | "Failed", then mark "Done".
const http = require('http');
const PAT = 'hsp_<your_token>';

async function gql(query, variables = {}) {
  const body = JSON.stringify({ query, variables });
  return new Promise((resolve, reject) => {
    const req = http.request(
      { hostname: 'localhost', port: 4000, path: '/graphql', method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': \`Bearer \${PAT}\`,
                   'Content-Length': Buffer.byteLength(body) } },
      res => {
        let d = '';
        res.on('data', c => d += c);
        res.on('end', () => {
          const p = JSON.parse(d);
          if (p.errors) throw new Error(p.errors[0].message);
          resolve(p.data);
        });
      }
    );
    req.on('error', reject);
    req.write(body); req.end();
  });
}

async function poll(queryId, intervalMs = 10000, timeoutMs = 300000) {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    await new Promise(r => setTimeout(r, intervalMs));
    const { getContentQueryById: q } = await gql(
      \`query($id: String!) { getContentQueryById(queryId: $id) {
        _id title metaDescription status
        content_score titleAccuracy headingsScore termsCoverage wordsCount
      }}\`,
      { id: queryId }
    );
    console.log(\`  Polling... status=\${q.status}\`);
    if (q.status === 'Generated' || q.status === 'Failed') return q;
  }
  throw new Error('Timed out waiting for blog generation');
}

async function main() {
  console.log('Triggering automated blog generation...');

  // Step 1: fire mutation — returns immediately with status "Processing"
  const { automateGenerateBlog: job } = await gql(
    \`mutation($d: AutoBlogInput!) {
      automateGenerateBlog(data: $d) { _id queryName status }
    }\`,
    {
      d: {
        query: "best CRM software for startups 2026",
        locations: ["us"],
        wordCount: 3000,
        includeImages: false,
        includeToc: true,
        includeFaq: true,
        requirements: "Focus on pricing comparisons. Target SaaS founders. Avoid enterprise tools."
      }
    }
  );
  console.log(\`  queryId: \${job._id}  status: \${job.status}\`);  // "Processing"

  // Step 2: poll until done
  const blog = await poll(job._id);

  if (blog.status === 'Failed') {
    console.error('Blog generation failed — check server logs for queryId:', job._id);
    process.exit(1);
  }

  console.log('Blog generated!');
  console.log('  ID:    ', blog._id);
  console.log('  Title: ', blog.title);
  console.log('  Score: ', blog.content_score);
  console.log('  Words: ', blog.wordsCount);

  // Step 3: mark Done so it appears in the UI "Done" tab
  await gql(
    \`mutation($id: String!, $s: String!) { updateContentQueryStatus(queryId: $id, status: $s) { _id status } }\`,
    { id: blog._id, s: 'Done' }
  );
  console.log('  Marked Done.');
}

main().catch(console.error);`}</pre>
              </CodeBlock>

              <Divider sx={{ my: 3 }} />

              <SubTitle>Node.js — Manual step-by-step blog generation script</SubTitle>
              <Prose sx={{ mb: 1 }}>
                Requires token scopes: <Chip label="CONTENT_WRITE" size="small" sx={{ fontSize: 11, height: 20, mx: 0.5 }} />
                <Chip label="KEYWORD_RESEARCH" size="small" sx={{ fontSize: 11, height: 20, mx: 0.5 }} />
                <Chip label="BLOG_GENERATE" size="small" sx={{ fontSize: 11, height: 20, mx: 0.5 }} />
              </Prose>
              <CodeBlock>
                <CopyButton text={`#!/usr/bin/env node
const http = require('http');
const PAT = 'hsp_<your_token>';
const URL  = 'http://localhost:4000/graphql';

async function gql(query, variables = {}) {
  const body = JSON.stringify({ query, variables });
  return new Promise((resolve, reject) => {
    const req = http.request({
      hostname: 'localhost', port: 4000, path: '/graphql', method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': \`Bearer \${PAT}\`, 'Content-Length': Buffer.byteLength(body) },
    }, res => { let d=''; res.on('data',c=>d+=c); res.on('end',()=>{const p=JSON.parse(d); if(p.errors) throw new Error(p.errors[0].message); resolve(p.data); }); });
    req.on('error', reject); req.write(body); req.end();
  });
}

async function main() {
  // 1. Create content query
  const { createContentQuery: q } = await gql(\`mutation { createContentQuery(queryName: "AI SEO 2026") { _id competitors { _id googleRank } } }\`);
  const queryId = q._id;

  // 2. Select top 10 competitors
  const top10 = q.competitors.sort((a,b)=>(a.googleRank??999)-(b.googleRank??999)).slice(0,10).map(c=>c._id);
  await gql(\`mutation($d:UpdateCompetitorsInput!){updateSelectedCompetitorsAddKeywords(data:$d){_id}}\`, { d: { queryId, competitorIds: top10, locations: ['in'] } });

  // 3. Generate title
  const { generateTitleByQueryId: title } = await gql(\`query($id:String!){generateTitleByQueryId(queryId:$id)}\`, { id: queryId });

  // 4. Generate meta description
  const { generateMetaDescriptionByQueryId: meta } = await gql(\`query($id:String!){generateMetaDescriptionByQueryId(queryId:$id)}\`, { id: queryId });

  // 5. Generate outline
  const { generateOutline: outline } = await gql(\`mutation($d:generateOutlineInput!){generateOutline(data:$d)}\`, { d: { queryId, title, BlogDescription: meta, wordCount: 2000 } });
  const headings = outline.outline.flatMap(s => [\`h2:\${s.h2}\`, ...(s.h3||[]).map(h=>\`h3:\${h}\`)]);

  // 6. Generate article
  const { generateArticleFromOutline: html } = await gql(\`mutation($d:generateArticleFromOutlineInput!){generateArticleFromOutline(data:$d)}\`, { d: { queryId, title, BlogDescription: meta, outlineHeadings: headings, includeImages: 'yes', wordCount: 2000 } });

  // 7. Save HTML
  await gql(\`mutation($d:updateBlogDescriptionByIdInput!){updateBlogDescriptionById(data:$d){_id}}\`, { d: { queryId, htmlDescription: html } });

  // 8. Analyse & score (also saves title + metaDescription)
  const { analyseAndUpdateBlog: scored } = await gql(\`mutation($d:AnalyseBlogandScoreInput!){analyseAndUpdateBlog(data:$d){_id content_score}}\`, { d: { queryId, title, html, metaDescription: meta } });
  console.log('Score:', scored.content_score);

  // 9. Mark as Done
  await gql(\`mutation{updateContentQueryStatus(queryId:"\${queryId}",status:"Done"){_id status}}\`);
  console.log('Done! queryId:', queryId);
}

main().catch(console.error);`} />
                <pre style={{ margin: 0, color: "#e2e8f0", fontSize: 12 }}>{`#!/usr/bin/env node
const http = require('http');
const PAT = 'hsp_<your_token>';

async function gql(query, variables = {}) {
  const body = JSON.stringify({ query, variables });
  return new Promise((resolve, reject) => {
    const req = http.request({
      hostname: 'localhost', port: 4000, path: '/graphql', method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': \`Bearer \${PAT}\`,
        'Content-Length': Buffer.byteLength(body),
      },
    }, res => {
      let d = '';
      res.on('data', c => d += c);
      res.on('end', () => {
        const p = JSON.parse(d);
        if (p.errors) throw new Error(p.errors[0].message);
        resolve(p.data);
      });
    });
    req.on('error', reject);
    req.write(body); req.end();
  });
}

async function main() {
  // Step 1 — create content query (auto-fetches SERP competitors)
  const { createContentQuery: q } = await gql(
    \`mutation { createContentQuery(queryName: "AI SEO 2026") {
       _id competitors { _id googleRank } } }\`
  );
  const queryId = q._id;

  // Step 2 — select top 10 competitors, set location
  const top10 = [...q.competitors]
    .sort((a, b) => (a.googleRank ?? 999) - (b.googleRank ?? 999))
    .slice(0, 10).map(c => c._id);
  await gql(\`mutation($d: UpdateCompetitorsInput!) {
    updateSelectedCompetitorsAddKeywords(data: $d) { _id } }\`,
    { d: { queryId, competitorIds: top10, locations: ['in'] } }
  );

  // Step 3 — generate title
  const { generateTitleByQueryId: title } = await gql(
    \`query($id: String!) { generateTitleByQueryId(queryId: $id) }\`,
    { id: queryId }
  );

  // Step 4 — generate meta description
  const { generateMetaDescriptionByQueryId: meta } = await gql(
    \`query($id: String!) { generateMetaDescriptionByQueryId(queryId: $id) }\`,
    { id: queryId }
  );

  // Step 5 — generate outline (returns JSON)
  const { generateOutline: outlineJson } = await gql(
    \`mutation($d: generateOutlineInput!) { generateOutline(data: $d) }\`,
    { d: { queryId, title, BlogDescription: meta, wordCount: 2000 } }
  );
  const headings = outlineJson.outline.flatMap(s => [
    \`h2:\${s.h2}\`,
    ...(s.h3 || []).map(h => \`h3:\${h}\`),
  ]);

  // Step 6 — generate article HTML
  const { generateArticleFromOutline: html } = await gql(
    \`mutation($d: generateArticleFromOutlineInput!) { generateArticleFromOutline(data: $d) }\`,
    { d: { queryId, title, BlogDescription: meta, outlineHeadings: headings,
            includeImages: 'yes', wordCount: 2000 } }
  );

  // Step 7 — save HTML
  await gql(\`mutation($d: updateBlogDescriptionByIdInput!) {
    updateBlogDescriptionById(data: $d) { _id } }\`,
    { d: { queryId, htmlDescription: html } }
  );

  // Step 8 — analyse & score (persists title + meta to DB)
  const { analyseAndUpdateBlog: scored } = await gql(
    \`mutation($d: AnalyseBlogandScoreInput!) {
      analyseAndUpdateBlog(data: $d) { _id content_score } }\`,
    { d: { queryId, title, html, metaDescription: meta } }
  );
  console.log('Content score:', scored.content_score);

  // Step 9 — mark Done (appears in UI)
  await gql(\`mutation {
    updateContentQueryStatus(queryId: "\${queryId}", status: "Done") { _id status } }\`
  );
  console.log('Complete! queryId:', queryId, '| title:', title);
}

main().catch(console.error);`}</pre>
              </CodeBlock>
            </SectionCard>

          </Box>
        </Box>
      </Container>
    </PageWrapper>
  );
}
